import {
    Observable
} from "rxjs";
import {
    map
} from "rxjs/operators";

import {
    Injectable, inject
} from "@angular/core";
import {
    BulkResponse,
    BulkUtil,
    CreateRequest,
    DeleteRequest,
    DeleteResponse,
    GetResponse,
    MarketArea,
    MarketAreaSearchFilterOptions,
    RequestUtil,
    SearchResponse,
    UpdateRequest
} from "@shopliftr/common-js/shared";
import {
    ISearchSort,
    ModelAction
} from "@shopliftr/common-js/data";
import { IFullAccessInternalApiClientService } from "../../../../interfaces/internal-api-client";
import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";
import { ListUtil } from "../../../../list/util/list-util";

/**
 * API Client Service for MarketArea entities.
 *
 * Implements the **Repository Pattern** (API Client Service), providing a clean abstraction
 * for CRUD operations and search functionality for MarketArea entities. Delegates HTTP
 * communication to InternalApiRequestService while encapsulating MarketArea-specific
 * request/response handling.
 *
 * Note: MarketArea extends AbstractElasticsearchModel and does not have a `cloneForAction`
 * instance method. Fields are passed directly to create/update requests.
 *
 * @pattern Repository Pattern / API Client Service
 * @providedIn root
 * @implements {IFullAccessInternalApiClientService<MarketArea>}
 */
@Injectable({ providedIn: "root" })
export class MarketAreaApiClientService implements IFullAccessInternalApiClientService<MarketArea> {

    readonly basePath = "market-area";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieve multiple MarketArea entities by their IDs in a single bulk request.
     *
     * @param ids Array of unique identifiers to retrieve
     * @returns Observable emitting a BulkResponse containing the requested entities
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<MarketArea>(id, MarketArea.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieve a single MarketArea by its unique identifier.
     *
     * @param id The unique identifier of the entity to retrieve
     * @returns Observable emitting the MarketArea entity
     */
    get$(id: string): Observable<MarketArea> {

        const request = RequestUtil.createGetRequest<void>(id, MarketArea.modelName, ModelAction.ClientGet);

        return this._internalApiRequestService.get$<MarketArea>(request, this.basePath).pipe(
            map((response: GetResponse<MarketArea>) => response.payload)
        );
    }

    /**
     * Retrieve all MarketArea entities matching the provided filter criteria.
     *
     * @param filterOptions The search criteria to apply
     * @param sort Array of sort criteria to apply (optional)
     * @returns Observable emitting an array of all matching entities
     */
    getAll$(filterOptions: MarketAreaSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<MarketArea>> {

        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            MarketArea.modelName,
            undefined,
            undefined,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.getAll$(request, this.basePath);
    }

    /**
     * Search for MarketArea entities with pagination support.
     *
     * @param filterOptions The search criteria to apply
     * @param page Zero-based page index for pagination (optional)
     * @param pageSize Number of items per page (optional)
     * @param sort Array of sort criteria to apply (optional)
     * @returns Observable emitting search results with pagination metadata
     */
    search$(
        filterOptions: MarketAreaSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<MarketArea>> {

        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            MarketArea.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.search$(request, this.basePath);
    }

    /**
     * Create a new MarketArea entity.
     *
     * Note: MarketArea does not have a `cloneForAction` instance method, so fields
     * are passed directly to the create request.
     *
     * @param fields The entity data to create
     * @returns Observable emitting the created entity
     */
    create$(fields: MarketArea): Observable<MarketArea> {

        const createRequest = RequestUtil.createCreateRequest<MarketArea, void>(
            ModelAction.ClientCreate,
            fields
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Create multiple MarketArea entities in a single bulk request.
     *
     * @param items Array of create requests
     * @returns Observable emitting a BulkResponse with the results
     */
    bulkCreate$(items: Array<CreateRequest<MarketArea, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Update an existing MarketArea entity.
     *
     * Note: MarketArea does not have a `cloneForAction` instance method, so fields
     * are passed directly to the update request.
     *
     * @param id The unique identifier of the entity to update
     * @param version The current version for optimistic locking
     * @param fields The entity fields to update (optional)
     * @param removeFields Array of field names to remove (optional)
     * @returns Observable emitting the updated entity
     */
    update$(id: string, version: number, fields?: MarketArea, removeFields?: Array<string>): Observable<MarketArea> {

        const updateRequest: UpdateRequest<MarketArea> = RequestUtil.createUpdateRequest(
            id, ModelAction.ClientUpdate, fields, version, removeFields
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Update a MarketArea with changes computed from original and updated models.
     *
     * @param original The original entity
     * @param updated The updated entity with changes applied
     * @returns Observable emitting the updated entity
     */
    updateWithChanges$(original: MarketArea, updated: MarketArea): Observable<MarketArea> {
        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }

    /**
     * Update multiple MarketArea entities in a single bulk request.
     *
     * @param requests Array of update requests
     * @returns Observable emitting a BulkResponse with the results
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Delete a MarketArea entity by ID and version.
     *
     * @param id The unique identifier of the entity to delete
     * @param version The current version for optimistic locking
     * @returns Observable emitting the delete response
     */
    delete$(id: string, version: number): Observable<DeleteResponse> {
        const deleteRequest = RequestUtil.createDeleteRequest<MarketArea>(
            id, MarketArea.modelName, ModelAction.ClientDelete, version
        );
        return this._internalApiRequestService.delete$(deleteRequest, this.basePath);
    }

    /**
     * Delete multiple MarketArea entities in a single bulk request.
     *
     * @param items Array of delete requests
     * @returns Observable emitting a BulkResponse with the results
     */
    bulkDelete$(items: Array<DeleteRequest<unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }
}
